import styled from '@emotion/styled/macro';
import { DataTableStyles, Variables } from '@enter/core';

/**
 * Colour comes from `Variables` only (NS-2).
 *
 * Two traps this file deliberately avoids: `Warning*` is the RED family and `Attention*` is
 * ORANGE — the names do not say so — and `*Mild*` and `*Medium*` are BOTH surface ramps, so
 * pairing them foreground/background gives about 1.2:1 and type-checks perfectly.
 */

export const Cell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

export const Name = styled.span`
    font-weight: 600;
    color: ${Variables.ThemeLight.TextStrong};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const Sub = styled.span`
    font-size: 11px;
    color: ${Variables.ThemeLight.TextMild};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

/** Halt is the exceptional state, so it is a badge rather than a column reading "Active" 400 times. */
export const Halted = styled.span`
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    padding: 1px 6px;
    border-radius: 4px;
    font: 600 10px/1.6 var(--font-sans, inherit);
    white-space: nowrap;
    letter-spacing: 0.04em;
    color: ${Variables.ThemeLight.WarningInverted4};
    background: ${Variables.ThemeLight.WarningMild3};
`;

export const Panel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 18px;
    width: 100%;
    padding: 16px 20px 20px;
    background: ${Variables.ThemeLight.Background1};
`;

export const Section = styled.section`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
`;

export const SectionHead = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
`;

export const SectionTitle = styled.h4`
    margin: 0;
    font: 600 11px/1.4 var(--font-sans, inherit);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.TextMild};
`;

export const SectionMeta = styled.span`
    font-size: 11px;
    color: ${Variables.ThemeLight.TextMild};
    white-space: nowrap;
`;

export const Lanes = styled.div`
    display: grid;
    grid-template-columns: minmax(150px, 1fr) auto minmax(0, 2fr);
    gap: 6px 14px;
    align-items: center;
`;

export const LaneName = styled.span`
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const LaneDetail = styled.span`
    font-size: 11px;
    color: ${Variables.ThemeLight.TextMild};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const Cards = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

export const Card = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px 12px;
    border: 1px solid ${Variables.ThemeLight.Border1};
    border-radius: 6px;
    background: ${Variables.ThemeLight.Background2};
    min-width: 0;
`;

export const Tag = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 3px 8px;
    border-radius: 5px;
    font: 500 11px/1.4 var(--font-sans, inherit);
    color: ${Variables.ThemeLight.TextMedium};
    background: ${Variables.ThemeLight.Background2};
    border: 1px solid ${Variables.ThemeLight.Border1};
`;

export const Muted = styled.span`
    font-size: 12px;
    color: ${Variables.ThemeLight.TextMild};
`;

/**
 * The toolbar row: search on the left, export and expand/collapse on the right.
 *
 * Ported from the support screen so the two apps look like one product. Jordan Kelley,
 * 2026-08-28, on this page: *"why does the platform look so much different than the support
 * tool for payers?"* — his screenshot showed 1131 payers and no way to search them.
 */
export const Toolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px;
`;

/**
 * Shrinks; does not wrap.
 *
 * `ControlsPanel` is `flex-wrap: wrap` with `justify-content: space-between`, and this is now a
 * third child between the Filter button and the pagination group. With a fixed 380px basis the
 * row overflowed and the nav group dropped to a second line, leaving the search stranded on the
 * first — the two-row layout this change existed to remove, only uglier.
 *
 * `flex: 1 1 200px` with a floor lets it give up width first, and `margin-right: auto` keeps it
 * beside Filter instead of floating into the middle of the gap.
 */
/**
 * The slot the search sits in, and the reason it is a separate element.
 *
 * Two requirements pull against each other inside the shared controls row, which is
 * flex-wrap with space-between. An auto right margin keeps the box anchored beside the Filter
 * button when the row wraps — without it the search strands itself at the far right while
 * everything else drops to a second line. But an auto margin gets no free space, because the
 * navigation container grows, so the box ends flush against the pagination pill and the two
 * read as overlapping.
 *
 * A wrapper gets both: the auto margin does the anchoring, the padding guarantees the gap.
 * Measured at 1440 and 1280 rather than reasoned about, after three passes that fixed one
 * width and broke the other.
 */
export const SearchSlot = styled.div`
    display: flex;
    flex: 0 1 336px;
    min-width: 166px;
    margin-right: auto;
    padding-right: 16px;
`;

export const Search = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    /*
     * Shrink, never grow. With flex-grow 1 it swallowed all the free space in the row, so the
     * auto right margin had none left and the box ended flush against the pagination pill,
     * which reads as the two overlapping. Fixed basis, free space goes to the margin, and it
     * still gives up width before anything wraps.
     *
     * (No backticks in here. Inside a styled template literal they end the string — third time
     * that has broken this build.)
     */
    flex: 1 1 auto;
    min-width: 0;
    padding: 0 12px;
    height: 38px;
    border-radius: 8px;
    border: 1px solid ${Variables.ThemeLight.Border1};
    background: ${Variables.ThemeLight.Background1};
    color: ${Variables.ThemeLight.TextMild};

    :focus-within {
        border-color: ${Variables.ThemeLight.PrimaryConstant6};
        background: ${Variables.ThemeLight.Background3};
    }
`;

export const SearchInput = styled.input`
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: ${Variables.ThemeLight.TextStrong};

    ::placeholder {
        color: ${Variables.ThemeLight.TextXMild};
    }
`;

export const ToolbarRight = styled.div`
    display: flex;
    flex: none;
    align-items: center;
    gap: 14px;
`;

/** Chevron in the expander column; rotates when the row is open. */
export const Chevron = styled.span<{ expanded: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${Variables.ThemeLight.TextMild};
    transition: transform 120ms ease-out;
    transform: rotate(${({ expanded }) => (expanded ? '90deg' : '0deg')});
`;

const V = Variables.ThemeLight;

/**
 * The four-column lane table: Data / Direction / Path / Goes to.
 *
 * Separate from `Lanes` (three columns) rather than widening it, because `Lanes` also lays out
 * the drop-to-paper rows, which have three cells. One grid serving both would silently
 * misalign whichever section it was not sized for.
 */
export const LaneTable = styled.div`
    border-radius: 8px;
    border: 1px solid ${V.Border1};
    background: ${V.Background3};
    overflow: hidden;
`;

export const LaneRow = styled.div<{ header?: boolean }>`
    display: grid;
    grid-template-columns: minmax(0, 1.6fr) 120px 140px minmax(0, 2.4fr);
    gap: 16px;
    align-items: center;
    padding: ${({ header }) => (header ? '10px 16px' : '12px 16px')};

    & + & {
        border-top: 1px solid ${V.Border1};
    }
`;

export const LaneHeadCell = styled.span`
    font: 600 10px/1.4 var(--font-sans, inherit);
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: ${V.TextMild};
`;

export const LaneCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

/** The small second line under a lane's name — "837 professional / institutional". */
export const LaneSub = styled.span`
    font-size: 11px;
    color: ${V.TextMild};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

/** Inbound / Outbound / Two-way, with its arrow. Remittance is the only inbound lane. */
export const Direction = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    color: ${V.TextMedium};
    white-space: nowrap;
`;

/**
 * "ALWAYS PAPER" — the categories X12 cannot carry at all (NS-7).
 *
 * Neutral, not a warning: reconsiderations going by post is correct behaviour, not a
 * misconfiguration a provider should ask us to fix. Colouring it like an alert would produce
 * exactly that support ticket.
 */
export const Badge = styled.span`
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    padding: 1px 6px;
    border-radius: 4px;
    font: 600 9px/1.6 var(--font-sans, inherit);
    letter-spacing: 0.06em;
    color: ${V.TextMedium};
    background: ${V.Background1};
    border: 1px solid ${V.Border1};
`;

export const CardMeta = styled.span`
    font-size: 11px;
    color: ${V.TextMild};
`;

/** Two columns, as the design draws it. Nine transactions means the last row holds one. */
export const EdiGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-top: 10px;
`;

export const EdiTile = styled.div<{ supported: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 6px;
    background: ${({ supported }) => (supported ? V.PrimaryTranslated1 : V.Background1)};
    border: 1px solid ${({ supported }) => (supported ? V.PrimaryMild1 : V.Border1)};
`;

export const EdiTileIcon = styled.span<{ supported: boolean }>`
    display: inline-flex;
    flex: none;
    color: ${({ supported }) => (supported ? V.SuccessConstant6 : V.TextXMild)};
`;

export const EdiTileText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
`;

export const EdiCode = styled.span<{ supported: boolean }>`
    font: 600 10px/1.3 var(--font-mono, monospace);
    letter-spacing: 0.03em;
    color: ${({ supported }) => (supported ? V.TextMedium : V.TextXMild)};
`;

export const EdiLabel = styled.span<{ supported: boolean }>`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 11px;
    color: ${({ supported }) => (supported ? V.TextStrong : V.TextMild)};
`;

/** The row's one control: opens the read-only detail drawer. */
export const DetailButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: ${V.TextMild};
    cursor: pointer;

    :hover {
        color: ${V.TextStrong};
        background: ${V.Background1};
    }
`;

/**
 * The support screen's expanded-row shape: a two-column top grid, then the lane table full
 * width beneath it. Matched deliberately — the two panels answer the same question and reading
 * them side by side should not feel like reading two products.
 */
export const TopGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 24px;
    align-items: start;
`;

export const Column = styled.div`
    display: flex;
    flex-direction: column;
    gap: 18px;
    min-width: 0;
`;

/** Category icon sitting inline with a lane name, as the design draws it. */
export const InlineIcon = styled.span`
    display: inline-flex;
    align-items: center;
    margin-right: 6px;
    vertical-align: -2px;
    color: ${V.TextMild};
`;

/** Name, its icon and the ALWAYS PAPER badge on ONE line — stacked, they broke the grid rows. */
export const LaneNameRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
`;

/**
 * The shared table's header, restyled for this screen only.
 *
 * Support scopes exactly this rule on its own card; provider had no equivalent, so the two
 * screens disagreed about whether column captions are uppercase. Scoped with an emotion
 * component selector rather than changed in `data-table.styles.tsx`, which every table in the
 * product renders.
 *
 * Captions wrap, as on Support (FE-106): "EMR payers" breaks at its space and "Reconsiderations"
 * at the soft hyphen its header carries, only when the column is too narrow. Centring keeps a
 * two-line caption level with one-line ones.
 */
/** The Halt tab's standing explanation, above the table inside the tab's panel. */
export const NoticeSlot = styled.div`
    margin-bottom: 12px;
`;

/**
 * The tab's panel — what `TabContainer` would hold if it could.
 *
 * Core's `TabContainer` wraps whatever it is given as children in a panel styled `display: none`
 * (`core/src/ui/tab-container/tab-container.styles.tsx`), so passing the table to it put the whole screen in the DOM
 * and off the screen: ten rows rendered, none visible. The page keeps its own panel instead, with the `role` and the
 * `id` each tab's `aria-controls` names, so the strip still points at something real.
 *
 * `min-height: 0` because the table scrolls inside it: without it a flex child refuses to shrink below its content
 * and the card grows past the viewport instead of the body scrolling.
 */
export const TabPanel = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

export const TableScope = styled.div`
    ${DataTableStyles.TableHeader} {
        display: flex;
        align-items: center;
        white-space: normal;
        border-right: none;
        font: 600 10px/1.4 var(--font-sans, inherit);
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: ${V.TextMild};
    }
`;
